import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { ShieldCheck, XCircle, Search } from "lucide-react";

export default function VerifyCertificate() {
  const { certId } = useParams();
  const [code, setCode] = useState(certId || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const verify = async (id) => {
    if (!id) return;
    setLoading(true);
    setSearched(true);
    try {
      const r = await api.verifyCertificate(id.trim());
      setResult(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (certId) verify(certId); }, [certId]);

  return (
    <div className="max-w-xl mx-auto px-6 py-20" data-testid="verify-page">
      <div className="asf-overline mb-3">Certificate verification</div>
      <h1 className="font-display text-4xl font-bold tracking-tight">Verify a certificate</h1>
      <p className="text-slate-500 mt-3">Enter a certificate ID to confirm its authenticity.</p>

      <form onSubmit={(e) => { e.preventDefault(); verify(code); }} className="flex gap-3 mt-8">
        <input data-testid="verify-input" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="e.g. ASF-1A2B3C4D"
          className="flex-1 rounded-xl bg-asf-app border border-slate-200 px-4 py-3 outline-none focus:border-asf-orange font-mono uppercase" />
        <button data-testid="verify-submit" className="btn-primary px-6 flex items-center gap-2"><Search className="h-4 w-4" /> Verify</button>
      </form>

      {loading && <p className="text-slate-500 mt-6">Checking…</p>}
      {!loading && searched && result && (
        result.valid ? (
          <div className="asf-card p-8 mt-8 border-green-500/30" data-testid="verify-valid">
            <ShieldCheck className="h-10 w-10 text-green-400 mb-4" />
            <h2 className="font-display text-2xl font-bold">Valid Certificate</h2>
            <dl className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Certificate ID</dt><dd className="font-mono text-asf-orange">{result.certificate.certificate_id}</dd></div>
              <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Student</dt><dd>{result.certificate.student_name}</dd></div>
              <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Course</dt><dd>{result.certificate.course_title}</dd></div>
              <div className="flex justify-between border-b border-slate-100 pb-2"><dt className="text-slate-500">Duration</dt><dd>{result.certificate.duration_hours} hours</dd></div>
              <div className="flex justify-between"><dt className="text-slate-500">Issued</dt><dd>{fmtDate(result.certificate.issued_at)}</dd></div>
            </dl>
          </div>
        ) : (
          <div className="asf-card p-8 mt-8 border-red-500/30" data-testid="verify-invalid">
            <XCircle className="h-10 w-10 text-red-400 mb-4" />
            <h2 className="font-display text-2xl font-bold">Not Found</h2>
            <p className="text-slate-500 mt-2">No certificate matches that ID.</p>
          </div>
        )
      )}
    </div>
  );
}
